/*
 * Module for the intent example editing modal
 */

define([
  "marionette",
  "messaging/messenger",
  "modules/topicsSplit/topic/topicExampleModal/topicExampleModalView",
  "modules/topicsSplit/topic/topicExampleModal/exampleLocale/exampleLocaleModule",
  "modules/topicsSplit/topic/topicExampleModal/exampleEditor/exampleEditorModule",
  "modules/topicsSplit/topic/topicExampleModal/exampleTranslation/exampleTranslationModule",
  "modules/topicsSplit/topic/topicExampleModal/strengthGauge/strengthGaugeModule",
  "modules/topicsSplit/topic/topicExampleModal/suppression/suppressionModule",
], function (
  Marionette,
  Messenger,
  TopicExampleModalView,
  ExampleLocaleModule,
  ExampleEditorModule,
  ExampleTranslationModule,
  StrengthGaugeModule,
  SuppressionModule,
) {
  "use strict";

  return Marionette.Controller.extend({
    initialize: function (options) {
      this.region = options.region;
      this.name = "topicExampleModal";
      this.selectedLocale = options.localeId;
      this._setUpVents();
    },
    //passed the example model to edit and an optional example string which can start as a starting point for an
    // example (say, from a learner transcript)
    show: function (exampleBundle) {
      this.failureThreshold = Messenger.reqres.request("example:failure:threshold");
      this.example = exampleBundle.example;
      // make sure the selected locale is set in the example model.
      // Otherwise, when creating a new example in any locale, locale ID will always be 1
      this.example.set("localeId", this.selectedLocale);
      this.learnerTranscript = exampleBundle.learnerTranscript;
      //save backup of the example
      this.example.backup();
      this.layout = new TopicExampleModalView.topicExampleModalLayout();
      this.locale = this.addSubModule(
        new ExampleLocaleModule({
          region: this.layout.locale,
          selectedLocale: this.selectedLocale,
          showOnlyEnglishLocales: false,
        }),
      );
      this.editor = this.addSubModule(new ExampleEditorModule({ region: this.layout.editor }));
      this.gauge = this.addSubModule(new StrengthGaugeModule({ region: this.layout.gauge }));
      this.translation = this.addSubModule(
        new ExampleTranslationModule({ region: this.layout.translation }),
      );
      this.suppression = this.addSubModule(
        new SuppressionModule({ region: this.layout.suppression }),
      );
      this.layout.on(
        "done",
        function () {
          this.layout.disableDoneButton();
          this.editor.stopStrengthPoll();
          // remove any extra whitespace and update example strength one last time to make sure a change wasn't made that
          // would hurt the example's strength
          this.editor.removeExtraWhitespace();
          Messenger.vent.trigger("update:strength", this.example.entries(), {
            closing: true,
          });
        },
        this,
      );
      this.layout.on(
        "cancel",
        function () {
          if (this.example.isNew()) {
            // A new example was just added to allow editing.  Since editing was cancelled, remove it
            this.example.destroy();
          } else {
            // Revert example back to original version
            this.example.restore();
          }
          //close modal
          Messenger.vent.trigger("close:modal");
        },
        this,
      );
      //initialize sub modules
      this.layout.on(
        "show",
        function () {
          this._showSubViews(this.example, this.learnerTranscript);
        },
        this,
      );
      this.showRegion(this.layout, { loading: true, entity: this.example.entries() });
    },
    _showSubViews: function (example, learnerTranscript) {
      this.gauge.show(example);
      this.translation.show(example);
      //if we have a learner transcript associated with this example, get the utterance and pass it to the editor to
      // show
      let startingExampleString = "";
      if (learnerTranscript) {
        startingExampleString = learnerTranscript.get("userUtterance");
      }
      this.locale.show(example);
      this.editor.show(example, startingExampleString);
      this.suppression.show(example);
    },
    _setUpVents: function () {
      Messenger.vent.on(
        "strength:updated",
        function (strengthScore, options) {
          //either enable or disable the done button depending on whether the strength score is below the failure
          // threshold
          if (strengthScore <= this.failureThreshold) {
            this.layout.disableDoneButton();
          } else {
            this.layout.enableDoneButton();
          }
          if (options && options.closing) {
            if (this.example.isEmpty()) {
              this.layout.disableDoneButton();
              //if the example is empty, just delete the example
              this.example.deleteModel({
                success: () => Messenger.vent.trigger("close:modal"),
              });
            } else if (strengthScore > this.failureThreshold) {
              this.layout.disableDoneButton();
              //Save the suppression status
              this.example.set("autocompleteSuppressed", this.suppression.getSuppressionStatus());
              //Set the selected locale
              this.example.set("localeId", this.locale.getSelectedLocale());
              this.example.applyDefaults();
              //wait until the defaults are applied before closing the modal
              this.example.once(
                "sync",
                function () {
                  //clean up entries
                  this.example.entries().prepareToSave();
                  //if we had a learner transcript associated with this example, then complete it
                  if (this.learnerTranscript) {
                    this.learnerTranscript.complete();
                  }
                  this.example.saveModel({
                    success: () => Messenger.vent.trigger("close:modal"),
                  });
                },
                this,
              );
            } else {
              this.editor.startStrengthPoll();
            }
          }
        },
        this,
      );
      Messenger.vent.on(
        "hide:example:buttons",
        function (isHidden) {
          // Notify the editor submodule to hide the optional and substitution buttons
          this.editor.hide(isHidden);
        },
        this,
      );
      Messenger.vent.on(
        "hide:translate:button",
        function (isHidden) {
          // Notify the editor submodule to hide the "Translate" button
          this.editor.hideTranslateButton(isHidden);
        },
        this,
      );
    },
  });
});
